import { useEffect, useState } from 'react';
import { Button, Drawer, Message, Pagination } from 'fave-ui';
import { Spin } from 'antd';
import { useQueryClient } from 'react-query';
import style from './style.module.css';
import ClosableTag from '../../../components/ClosableTag';
import { SearchBarExpandable } from '../../../components/common/index';
import { defaultPaginationConfig } from '../../../helpers/defaults';
import { PaginationSettingsType } from '../../../types/configTypes';
import { createOnPaginationChangeHandler } from '../../../utils/utilFunctions';
import {
  queryKeys,
  useFetchFavePassOutletsList,
  useMutateUnlinkFavePassOutlet,
} from '../../../services/FavePass';

type LinkedOutletsDrawerProps = {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (bool: boolean) => void;
  favePassId: number;
  enableViewOutletsList: boolean;
  enableUnlinkOutlets: boolean;
};

const linkedOutletsPaginationConfig = {
  page: defaultPaginationConfig.page,
  limit: 20,
};

const LinkedOutletsDrawer = ({
  isDrawerOpen,
  setIsDrawerOpen,
  favePassId,
  enableViewOutletsList,
  enableUnlinkOutlets,
}: LinkedOutletsDrawerProps) => {
  const queryClient = useQueryClient();
  const enableFetchOutletsList = isDrawerOpen && enableViewOutletsList;

  const [paginationConfig, setPaginationConfig] =
    useState<PaginationSettingsType>(linkedOutletsPaginationConfig);

  const [searchOutletValue, setSearchOutletValue] = useState('');

  const {
    data: linkedOutletsListData,
    isFetching: isFetchingLinkedOutlets,
    refetch: refetchLinkedOutlets,
  } = useFetchFavePassOutletsList(
    {
      favePassId,
      filter: `id_or_name=${searchOutletValue}`,
      ...paginationConfig,
    },
    { enabled: enableFetchOutletsList },
  );

  const { mutate: unlinkOutlet } = useMutateUnlinkFavePassOutlet();

  useEffect(() => {
    if (enableFetchOutletsList) refetchLinkedOutlets();
  }, [enableFetchOutletsList]);

  const paginationChangeHandler =
    createOnPaginationChangeHandler(setPaginationConfig);

  const LinkedOutletsPagination = () => (
    <Pagination
      className={style.linkedDealOutletsPagination}
      simple
      defaultCurrent={paginationConfig.page}
      pageSize={paginationConfig.limit}
      onChange={paginationChangeHandler}
      total={linkedOutletsListData?.count}
    />
  );

  const CloseDrawerButton = () => (
    <Button
      type="text"
      onClick={() => {
        queryClient.invalidateQueries(queryKeys.AvailableFavePassOutlets);
        queryClient.invalidateQueries(queryKeys.FavePassOutletSummary);

        setIsDrawerOpen(false);
      }}
    >
      Close
    </Button>
  );

  const handleUnlinkOutlet = async (
    favePassOutletId: number,
    favePassId: number,
  ) =>
    unlinkOutlet(
      { favePassId, favePassOutletId },
      {
        onSuccess: () => {
          Message.success({
            content: 'Outlet successfully unlinked!',
          });
          refetchLinkedOutlets();
        },
      },
    );

  const LinkedOutletsList = () => (
    <Spin spinning={isFetchingLinkedOutlets} wrapperClassName={style.spin}>
      <div>
        {linkedOutletsListData?.fave_pass_outlets.map(({ id, outlet }) => (
          <ClosableTag
            closable={enableUnlinkOutlets}
            isReversed
            key={id}
            onClose={() => handleUnlinkOutlet(id, favePassId)}
          >
            {outlet.name}
          </ClosableTag>
        ))}
      </div>
    </Spin>
  );

  return (
    <Drawer
      visible={isDrawerOpen}
      maskClosable={false}
      extra={<CloseDrawerButton />}
      title={'Linked Outlets'}
      placement="right"
      footer={<LinkedOutletsPagination />}
    >
      <SearchBarExpandable
        onChange={setSearchOutletValue}
        placeholder={'search here'}
        className={style.searchBar}
        alwaysOpen={true}
        alwaysShowBorder={true}
      />
      <LinkedOutletsList />
    </Drawer>
  );
};

export default LinkedOutletsDrawer;
